import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { InlineTip } from '../components/InlineTip';
import { HelpTooltip, HELP } from '../components/HelpTooltip';
import { Phone, Star, Users, FileText, Copy, Check, PhoneCall, PhoneMissed, Ban, UserX, Inbox, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const TABS = [
  { id: 'leads', label: 'Lead Handling', icon: Phone },
  { id: 'reviews', label: 'Review Engine', icon: Star },
  { id: 'referrals', label: 'Referrals & Retention', icon: Users },
  { id: 'report', label: 'Weekly Report', icon: FileText },
];

const OUTCOME_BUTTONS = [
  { id: 'booked', label: 'Booked', icon: PhoneCall, cls: 'border-lime text-lime hover:bg-lime hover:text-black' },
  { id: 'not_booked', label: 'Not Booked', icon: PhoneMissed, cls: 'border-orange text-orange hover:bg-orange hover:text-black' },
  { id: 'spam', label: 'Spam', icon: Ban, cls: 'border-gray text-gray hover:bg-gray hover:text-black' },
  { id: 'wrong_fit', label: 'Wrong Fit', icon: UserX, cls: 'border-red-400 text-red-400 hover:bg-red-400 hover:text-black' },
];

const CopyBtn = ({ text, testId }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-xs font-bold text-lime hover:text-white transition-colors shrink-0"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const StatCard = ({ label, value, accent, testId }) => (
  <div className="card" data-testid={testId}>
    <p className="text-light-gray text-sm">{label}</p>
    <p className={`text-4xl font-black ${accent || 'text-white'}`}>{value}</p>
  </div>
);

const TemplateCard = ({ title, text, testId }) => (
  <div className="card" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-white font-bold">{title}</h4>
      <CopyBtn text={text} testId={`${testId}-copy`} />
    </div>
    <p className="text-light-gray text-sm whitespace-pre-wrap leading-relaxed">{text}</p>
  </div>
);

const LeadHandlingTab = () => {
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [bookingLead, setBookingLead] = useState(null);
  const [jobValue, setJobValue] = useState('');

  const load = useCallback(() => {
    fetch(`${API}/api/growth/lead-handling`, { headers: authHeaders() }).then((r) => r.json()).then(setData).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const logCall = async (outcome, leadId, value) => {
    setBusy(true);
    try {
      await fetch(`${API}/api/growth/call-log`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          outcome, notes: leadId ? '' : note, lead_id: leadId || undefined,
          job_value: value ? Number(value) : undefined,
        }),
      });
      setNote('');
      setBookingLead(null);
      setJobValue('');
      load();
      window.dispatchEvent(new Event('results-updated'));
    } finally {
      setBusy(false);
    }
  };

  const startBooking = (leadId) => { setBookingLead(leadId); setJobValue(''); };
  const saveBooked = () => logCall('booked', bookingLead === 'manual' ? undefined : bookingLead, jobValue);

  if (!data) return <div className="text-light-gray py-12 text-center">Loading...</div>;

  const bandColor = { strong: 'text-lime', average: 'text-orange', needs_work: 'text-red-400', no_data: 'text-gray' }[data.band];

  return (
    <div data-testid="lead-handling-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card md:row-span-1" data-testid="lead-handling-score-card">
          <p className="text-light-gray text-sm mb-1 flex items-center gap-1.5">Lead Handling Score <HelpTooltip size={13} text="Of the real calls you got (Booked + Not Booked), the percentage you actually booked. Spam and Wrong Fit calls don't count against you. 70%+ is strong — answering fast is the easiest way to raise it." testId="help-lead-handling-score" /></p>
          <p className={`text-5xl font-black ${bandColor}`} data-testid="lead-handling-score">
            {data.score !== null ? `${data.score}%` : '—'}
          </p>
          <p className={`font-bold mt-1 ${bandColor}`}>{data.band_label}</p>
          <p className="text-xs text-muted mt-2">% of real calls (Booked vs Not Booked) that you booked. Spam and Wrong Fit don't count against you.</p>
        </div>
        <div className="card md:col-span-2">
          <p className="text-light-gray text-sm mb-3 flex items-center gap-1.5">All-Time Call Outcomes <HelpTooltip size={13} text="Every call you've logged, sorted by what happened. Booked = a real scheduled job (the number that pays the bills). Not Booked = a real customer you didn't win. Spam and Wrong Fit are noise — they don't hurt your score." testId="help-alltime-call-outcomes" /></p>
          <div className="grid grid-cols-4 gap-3 text-center">
            {OUTCOME_BUTTONS.map((o) => (
              <div key={o.id} data-testid={`outcome-count-${o.id}`}>
                <p className="text-3xl font-black text-white">{data.counts[o.id]}</p>
                <p className="text-xs text-light-gray mt-1">{o.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Manual call log — the 4 buttons from the PPC Playbook */}
      <div className="card mb-6" data-testid="call-log-panel">
        <h3 className="text-white font-bold text-lg mb-1">Log a Call</h3>
        <p className="text-light-gray text-sm mb-4">
          Every time an ad call comes in, mark it with one button — same four labels the Google Ads guide teaches.
        </p>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (e.g. 'AC repair call, quoted $450')"
          data-testid="call-log-note-input"
          className="w-full px-4 py-3 bg-slate-blue border border-divider rounded-lg text-white mb-4 focus:outline-none focus:border-construction-orange"
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {OUTCOME_BUTTONS.map((o) => (
            <button
              key={o.id}
              onClick={() => (o.id === 'booked' ? startBooking('manual') : logCall(o.id))}
              disabled={busy}
              data-testid={`call-log-btn-${o.id}`}
              className={`flex items-center justify-center gap-2 px-4 py-3.5 rounded-lg border-2 font-bold transition-colors bg-transparent ${o.cls}`}
            >
              <o.icon size={18} />
              {o.label}
            </button>
          ))}
        </div>
        {bookingLead === 'manual' && (
          <div className="mt-4 flex flex-wrap items-center gap-3 p-4 bg-slate-blue rounded-lg border border-lime/40" data-testid="manual-job-value-form">
            <p className="text-sm text-white font-bold">Booked it — what's the job worth?</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray text-sm">$</span>
              <input
                type="number" min="0" autoFocus value={jobValue}
                onChange={(e) => setJobValue(e.target.value)}
                placeholder="e.g. 450"
                data-testid="manual-job-value-input"
                className="w-36 pl-7 pr-3 py-2 bg-black border border-divider rounded-lg text-white text-sm focus:outline-none focus:border-lime"
              />
            </div>
            <button onClick={saveBooked} disabled={busy} data-testid="manual-job-value-save"
              className="px-4 py-2 rounded-lg bg-lime text-black text-sm font-bold hover:brightness-95">Save Job</button>
            <button onClick={() => logCall('booked')} disabled={busy} data-testid="manual-job-value-skip"
              className="px-3 py-2 rounded-lg border border-divider text-gray text-sm font-bold hover:text-white">Skip value</button>
          </div>
        )}
      </div>

      {/* Lead inbox from landing pages */}
      {data.lead_inbox.length > 0 && (
        <div className="card mb-6" data-testid="lead-inbox">
          <div className="mb-4">
            <InlineTip>Speed is the whole game on follow-up. Calling a lead within 5 minutes makes you ~9x more likely to book them than waiting an hour. Every lead below deserves a same-hour callback.</InlineTip>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Inbox size={18} className="text-lime" />
            <h3 className="text-white font-bold text-lg">New Leads From Your Landing Pages</h3>
          </div>
          <p className="text-light-gray text-sm mb-4">Call them back, then mark the outcome.</p>
          <div className="space-y-3">
            {data.lead_inbox.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-4 p-4 bg-slate-blue rounded-lg border border-divider" data-testid={`lead-row-${l.id}`}>
                <div className="flex-1 min-w-[220px]">
                  <p className="text-white font-bold">{l.name} <span className="text-lime font-semibold ml-2">{l.phone}</span></p>
                  <p className="text-light-gray text-sm">{l.issue || 'No details given'} · <span className="text-muted">{l.campaign_name}</span></p>
                </div>
                <div className="flex gap-2 items-center">
                  {bookingLead === l.id ? (
                    <div className="flex items-center gap-2" data-testid={`lead-${l.id}-job-value-form`}>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray text-xs">$</span>
                        <input
                          type="number" min="0" autoFocus value={jobValue}
                          onChange={(e) => setJobValue(e.target.value)}
                          placeholder="Job value"
                          data-testid={`lead-${l.id}-job-value-input`}
                          className="w-28 pl-6 pr-2 py-1.5 bg-black border border-divider rounded-md text-white text-xs focus:outline-none focus:border-lime"
                        />
                      </div>
                      <button onClick={saveBooked} disabled={busy} data-testid={`lead-${l.id}-save-booked`}
                        className="px-3 py-1.5 rounded-md bg-lime text-black text-xs font-bold">Save</button>
                      <button onClick={() => { setBookingLead(null); setJobValue(''); }} data-testid={`lead-${l.id}-cancel-booked`}
                        className="px-2 py-1.5 rounded-md border border-divider text-gray text-xs font-bold">Cancel</button>
                    </div>
                  ) : (
                    OUTCOME_BUTTONS.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => (o.id === 'booked' ? startBooking(l.id) : logCall(o.id, l.id))}
                        disabled={busy}
                        data-testid={`lead-${l.id}-mark-${o.id}`}
                        className={`px-3 py-1.5 rounded-md border text-xs font-bold transition-colors ${o.cls}`}
                      >
                        {o.label}
                      </button>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card" data-testid="lead-handling-insights">
          <h3 className="text-white font-bold mb-3">What Your Numbers Say</h3>
          <ul className="space-y-2">
            {data.insights.map((ins, i) => (
              <li key={i} className="text-light-gray text-sm leading-relaxed flex gap-2">
                <span className="text-lime shrink-0">→</span>{ins}
              </li>
            ))}
          </ul>
        </div>
        <div className="card" data-testid="call-log-recent">
          <h3 className="text-white font-bold mb-3">Recent Calls</h3>
          {data.recent.length === 0 ? (
            <p className="text-muted text-sm">Nothing logged yet.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
              {data.recent.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm border-b border-divider/50 pb-2">
                  <span className="text-light-gray">
                    <span className="text-white font-semibold">{data.outcome_labels[r.outcome]}</span>
                    {r.lead_name ? ` — ${r.lead_name}` : ''}{r.notes ? ` · ${r.notes}` : ''}
                  </span>
                  <span className="text-muted text-xs shrink-0 ml-3">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const QuickLogButton = ({ type, label, onLogged }) => {
  const [busy, setBusy] = useState(false);
  const log = async () => {
    setBusy(true);
    try {
      await fetch(`${API}/api/growth/log`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ type }) });
      onLogged();
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={log} disabled={busy} data-testid={`quick-log-${type}`}
      className="px-4 py-2.5 rounded-lg border-2 border-lime text-lime font-bold text-sm hover:bg-lime hover:text-black transition-colors">
      + {label}
    </button>
  );
};

const ReviewsTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    fetch(`${API}/api/growth/reviews`, { headers: authHeaders() }).then((r) => r.json()).then(setData).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-light-gray py-12 text-center">Loading...</div>;

  return (
    <div data-testid="reviews-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Jobs Booked" value={data.jobs_booked} testId="reviews-jobs-booked" help={HELP.bookedJobs} />
        <StatCard label="Review Requests Sent" value={data.review_requests_sent} testId="reviews-requests-sent" help="How many customers you've asked for a Google review. More asks = more reviews = more calls from strangers who trust you." />
        <StatCard label="Reviews Received" value={data.reviews_received} accent="text-lime" testId="reviews-received" help="New Google reviews that came in. Reviews are free marketing that works while you sleep." />
        <StatCard label="Request → Review Rate" value={data.conversion_rate !== null ? `${data.conversion_rate}%` : '—'} accent="text-orange" testId="reviews-conversion" help="Of the customers you asked, how many actually left a review. 20–30% is normal; asking right after a great job pushes it higher." />
      </div>

      <div className="card mb-6" data-testid="reviews-quick-log">
        <h3 className="text-white font-bold mb-1">Log It As It Happens</h3>
        <p className="text-light-gray text-sm mb-4">Manual mode — one click each time you send a request or a review lands.</p>
        <div className="flex flex-wrap gap-3">
          <QuickLogButton type="review_request_sent" label="Request Sent" onLogged={load} />
          <QuickLogButton type="review_received" label="Review Received" onLogged={load} />
        </div>
      </div>

      {!data.has_review_link && (
        <div className="card mb-6 border-orange/50" data-testid="reviews-no-link-warning">
          <p className="text-orange font-bold">Your Google review link isn't set.</p>
          <p className="text-light-gray text-sm mt-1">
            The templates below can't fill in your link until you add it on the{' '}
            <Link to="/brand" className="text-lime underline">My Brand page</Link>.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <TemplateCard title="Review Request — Text Message" text={data.templates.sms} testId="review-template-sms" />
        <TemplateCard title="Review Request — Email" text={`Subject: ${data.templates.email_subject}\n\n${data.templates.email_body}`} testId="review-template-email" />
      </div>

      <div className="card" data-testid="reviews-guidance">
        <h3 className="text-white font-bold mb-3">How the Pros Do It</h3>
        <ul className="space-y-2">
          {data.guidance.map((g, i) => (
            <li key={i} className="text-light-gray text-sm flex gap-2"><span className="text-lime shrink-0">→</span>{g}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ReferralsTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    fetch(`${API}/api/growth/referrals`, { headers: authHeaders() }).then((r) => r.json()).then(setData).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-light-gray py-12 text-center">Loading...</div>;

  return (
    <div data-testid="referrals-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Referral Asks Sent" value={data.referral_asks_sent} testId="referrals-asks" help="How many happy customers you've asked to send friends your way. Referred jobs close easier and haggle less." />
        <StatCard label="Referrals Received" value={data.referrals_received} accent="text-lime" testId="referrals-received" help="New customers who came because someone recommended you. The cheapest, warmest leads you'll ever get." />
        <StatCard label="Retention Messages Sent" value={data.retention_messages_sent} testId="referrals-retention-sent" help="Check-in texts/emails to past customers (tune-up reminders, seasonal offers). Staying in touch keeps them from calling someone else." />
        <StatCard label="Memberships Sold" value={data.memberships_sold} accent="text-orange" testId="referrals-memberships" help="Maintenance plans sold. Members call YOU first, every time — steady money in slow months." />
      </div>

      <div className="card mb-6" data-testid="referrals-quick-log">
        <h3 className="text-white font-bold mb-1">Log It As It Happens</h3>
        <p className="text-light-gray text-sm mb-4">Manual mode — one click per action so GritClicks can show what your customer list is worth.</p>
        <div className="flex flex-wrap gap-3">
          <QuickLogButton type="referral_ask_sent" label="Referral Ask Sent" onLogged={load} />
          <QuickLogButton type="referral_received" label="Referral Received" onLogged={load} />
          <QuickLogButton type="retention_message_sent" label="Retention Message Sent" onLogged={load} />
          <QuickLogButton type="membership_sold" label="Membership Sold" onLogged={load} />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <TemplateCard title="Referral Ask — Text Message" text={data.templates.referral_sms} testId="referral-template-sms" />
        <TemplateCard title="Win-Back / Maintenance Reminder" text={data.templates.retention_sms} testId="retention-template-sms" />
        <TemplateCard title="Membership Pitch" text={data.templates.membership_pitch} testId="membership-template" />
      </div>

      <div className="card" data-testid="referrals-guidance">
        <h3 className="text-white font-bold mb-3">Why This Beats Buying More Ads</h3>
        <ul className="space-y-2">
          {data.guidance.map((g, i) => (
            <li key={i} className="text-light-gray text-sm flex gap-2"><span className="text-lime shrink-0">→</span>{g}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const WeeklyReportTab = () => {
  const [report, setReport] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/growth/weekly-report`, { headers: authHeaders() }).then((r) => r.json()).then(setReport).catch(() => {});
  }, []);
  if (!report) return <div className="text-light-gray py-12 text-center">Building your report...</div>;

  const t = report.totals;
  const w = report.this_week;

  return (
    <div data-testid="weekly-report-tab">
      <div className="card mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6 pb-6 border-b border-divider">
          <div>
            <h2 className="text-2xl font-black text-white">Weekly Marketing Report</h2>
            <p className="text-light-gray text-sm mt-1">{report.company} · {report.period_start} — {report.period_end}</p>
            <p className="text-xs text-muted mt-1">The same report an agency would charge $5,000+/month to send you.</p>
          </div>
          <CopyBtn text={report.report_text} testId="weekly-report-copy" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8" data-testid="weekly-report-totals">
          <StatCard label="Ad Spend" value={`$${Number(t.spend).toLocaleString()}`} help={HELP.spend} />
          <StatCard label="Leads" value={t.leads} help={HELP.leads} />
          <StatCard label="Calls" value={t.calls} help={HELP.calls} />
          <StatCard label="Booked Jobs" value={t.booked_jobs} accent="text-lime" help={HELP.bookedJobs} />
          <StatCard label="Revenue" value={`$${Number(t.revenue).toLocaleString()}`} accent="text-orange" help={HELP.revenue} />
          <StatCard label="ROI" value={t.roi !== null ? `${t.roi}%` : '—'} accent={t.roi > 0 ? 'text-lime' : 'text-white'} help={HELP.roi} />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div data-testid="weekly-report-campaigns">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2"><TrendingUp size={18} className="text-lime" />Campaigns Running ({report.campaigns_running.length})</h3>
            {report.campaigns_running.length === 0 ? (
              <p className="text-muted text-sm">No campaigns deployed yet.</p>
            ) : (
              <ul className="space-y-2">
                {report.campaigns_running.map((c, i) => (
                  <li key={i} className="text-light-gray text-sm flex justify-between border-b border-divider/50 pb-2">
                    <span>{c.campaign_name} <span className="text-muted">({c.trade})</span></span>
                    <span className="text-lime font-bold">{c.booking_count} booked</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div data-testid="weekly-report-this-week">
            <h3 className="text-white font-bold mb-3">This Week's Activity</h3>
            <ul className="space-y-2 text-sm text-light-gray">
              <li className="flex justify-between border-b border-divider/50 pb-2"><span>New landing page leads</span><span className="text-white font-bold">{w.new_leads_from_landing_pages}</span></li>
              <li className="flex justify-between border-b border-divider/50 pb-2">
                <span>Calls logged</span>
                <span className="text-white font-bold">{w.calls_logged}
                  <span className="text-muted font-normal text-xs ml-2">
                    (B {w.calls_breakdown.booked} / NB {w.calls_breakdown.not_booked} / S {w.calls_breakdown.spam} / WF {w.calls_breakdown.wrong_fit})
                  </span>
                </span>
              </li>
              <li className="flex justify-between border-b border-divider/50 pb-2"><span>Review requests sent</span><span className="text-white font-bold">{w.review_requests_sent}</span></li>
              <li className="flex justify-between border-b border-divider/50 pb-2"><span>Reviews received</span><span className="text-lime font-bold">{w.reviews_received}</span></li>
              <li className="flex justify-between"><span>Referrals received</span><span className="text-orange font-bold">{w.referrals_received}</span></li>
            </ul>
            <p className="text-sm text-light-gray mt-4">
              Lead Handling Score:{' '}
              <span className="text-white font-black">{report.lead_handling.score !== null ? `${report.lead_handling.score}%` : '—'}</span>
              <span className="text-muted ml-1">({report.lead_handling.band_label})</span>
            </p>
          </div>
        </div>

        <div data-testid="weekly-report-next-actions">
          <h3 className="text-white font-bold mb-3">What To Do Next Week</h3>
          <ol className="space-y-2">
            {report.next_actions.map((a, i) => (
              <li key={i} className="text-light-gray text-sm flex gap-3">
                <span className="text-lime font-black shrink-0">{i + 1}.</span>{a}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

const ResultsBar = () => {
  const [results, setResults] = useState(null);

  useEffect(() => {
    const loadResults = () =>
      fetch(`${API}/api/growth/results`, { headers: authHeaders() })
        .then((r) => r.json()).then(setResults).catch(() => {});
    loadResults();
    window.addEventListener('results-updated', loadResults);
    return () => window.removeEventListener('results-updated', loadResults);
  }, []);

  const cards = [
    { testId: 'dashboard-results-leads', icon: Phone, label: 'Leads', value: results ? results.leads : '—', color: 'text-lime', sub: 'Every call and form that came in' },
    { testId: 'dashboard-results-jobs', icon: PhoneCall, label: 'Jobs Booked', value: results ? results.jobs_booked : '—', color: 'text-[#3b9eff]', sub: 'Leads you marked as booked' },
    { testId: 'dashboard-results-revenue', icon: TrendingUp, label: 'Revenue', value: results ? `$${Math.round(results.revenue).toLocaleString()}` : '—', color: 'text-[#ec6a1c]', sub: 'Dollar value of your booked jobs' },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="dashboard-results">
      {cards.map((c) => (
        <div key={c.label} className="card flex items-center justify-between" data-testid={c.testId}>
          <div>
            <p className="text-muted text-sm mb-1">{c.label}</p>
            <p className={`text-3xl font-black ${c.color}`}>{c.value}</p>
            <p className="text-xs text-gray mt-1">{c.sub}</p>
          </div>
          <div className={`p-3 bg-slate-blue rounded-xl ${c.color}`}><c.icon size={26} /></div>
        </div>
      ))}
    </div>
  );
};

const GrowthCenter = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'leads');

  return (
    <Layout variant="light">
      <div className="max-w-7xl mx-auto px-6 py-8" data-testid="growth-center-page">
        <h1 className="text-4xl font-black text-white mb-2">Growth Center</h1>
        <p className="text-light-gray mb-8">
          Everything after the click — handle leads, stack reviews, mine referrals, and read your own weekly report.
        </p>

        <ResultsBar />
        <div className="flex flex-wrap gap-2 mb-8 border-b border-divider pb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`growth-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-colors ${
                activeTab === t.id ? 'bg-lime text-black' : 'text-light-gray hover:bg-slate-blue hover:text-white'
              }`}
            >
              <t.icon size={17} />
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'leads' && <LeadHandlingTab />}
        {activeTab === 'reviews' && <ReviewsTab />}
        {activeTab === 'referrals' && <ReferralsTab />}
        {activeTab === 'report' && <WeeklyReportTab />}
      </div>
    </Layout>
  );
};

export default GrowthCenter;
